// Yeni FastAPI backend istemcisi.
//
// Kullanım:
//   import { api, legacyPhp } from "./api/client";
//   const { data } = await api.get("/is-emirleri/durum/Devam");
//
// - `api`       → yeni FastAPI (baseURL = REACT_APP_API_BASE + /api/v1)
// - `legacyPhp` → henüz porta edilmemiş / PDF render eden eski PHP sayfaları
// - MRP arama/BOM → api.get("/mrp/...") → external-api (ofis LAN'a tarayıcı gitmez)
//
// Ekranlar `api.get("/x")` ile FastAPI'ye bağlanır (gunluk_rapor.php / kirmizi_defter_rapor.php hariç .php API yok).

import axios from "axios";

// Cloud: same-origin /api/v1. Lokal: .env → http://localhost:8000
const _apiBase = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const LEGACY_PHP_BASE = (process.env.REACT_APP_LEGACY_PHP_BASE || "").replace(/\/$/, "") || "";

export const api = axios.create({
  baseURL: _apiBase ? `${_apiBase}/api/v1` : "/api/v1",
  withCredentials: true, // refresh_token + csrf cookie'leri
  timeout: 30000,
  // tip=garanti&tip=servis — FastAPI list[str] Query
  // axios 0.22: paramsSerializer fonksiyon olmalı (1.x {serialize} objesi değil)
  paramsSerializer: (params) => {
    const usp = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, val]) => {
      if (val == null || val === "") return;
      if (Array.isArray(val)) {
        val.forEach((item) => {
          if (item != null && item !== "") usp.append(key, String(item));
        });
      } else {
        usp.append(key, String(val));
      }
    });
    return usp.toString();
  },
});

// FastAPI CSRF: login sonrası csrf_token cookie'si set edilir, state-changing
// isteklerde aynı değeri X-CSRF-Token header'ına koy (double-submit).
api.interceptors.request.use((config) => {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  if (m && !["get", "head", "options"].includes((config.method || "").toLowerCase())) {
    config.headers["X-CSRF-Token"] = decodeURIComponent(m[1]);
  }
  return config;
});

export const legacyPhp = axios.create({
  baseURL: LEGACY_PHP_BASE,
  withCredentials: true,
  timeout: 30000,
});

/** @deprecated ofis 192.168.2.24 yerine api.get('/mrp/...') kullan */
export const mrpApi = api;

// PDF / HTML render eden eski sayfalar için tam URL (iframe/link src'si).
export const legacyPhpUrl = (path) => {
  const rel = String(path || "").replace(/^\//, "");
  return LEGACY_PHP_BASE ? `${LEGACY_PHP_BASE}/${rel}` : `/${rel}`;
};

/** Login user.id — memory + localStorage (render anında storage boş kalmasın). */
const SESSION_USER_KEYS = ["ssh_user_id", "user_id"];
let _sessionUserId = null;

export function setSessionUserId(userId) {
  if (userId == null || userId === "" || userId === "null" || userId === "undefined") {
    _sessionUserId = null;
    if (typeof localStorage !== "undefined") {
      SESSION_USER_KEYS.forEach((k) => localStorage.removeItem(k));
    }
    return;
  }
  const n = Number(userId);
  if (!Number.isFinite(n) || n <= 0) {
    _sessionUserId = null;
    return;
  }
  _sessionUserId = n;
  if (typeof localStorage !== "undefined") {
    const v = String(n);
    SESSION_USER_KEYS.forEach((k) => localStorage.setItem(k, v));
  }
}

export function getSessionUserId() {
  if (_sessionUserId != null && _sessionUserId > 0) return _sessionUserId;
  if (typeof localStorage === "undefined") return null;
  for (const k of SESSION_USER_KEYS) {
    const v = localStorage.getItem(k);
    if (v && v !== "null" && v !== "undefined") {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) {
        _sessionUserId = n;
        return n;
      }
    }
  }
  return null;
}

/** Günlük rapor URL — login user_id zorunlu (PHP is_emri_unread mark-read). */
export function gunlukRaporUrl(isEmriNo, userId) {
  const uid = userId != null && Number(userId) > 0 ? Number(userId) : getSessionUserId();
  const q = new URLSearchParams({ is_emri_no: String(isEmriNo || "") });
  if (uid != null) q.set("user_id", String(uid));
  try {
    if (localStorage.getItem("ct_lang") === "en") q.set("lang", "en");
  } catch (_) {
    /* ignore */
  }
  return legacyPhpUrl(`gunluk_rapor.php?${q.toString()}`);
}

export function visitReportUrl(raporId) {
  const q = new URLSearchParams();
  const uid = getSessionUserId();
  if (uid != null) q.set("user_id", String(uid));
  try {
    if (localStorage.getItem("ct_lang") === "en") q.set("lang", "en");
  } catch (_) {
    /* ignore */
  }
  const qs = q.toString();
  return `/pdf/ziyaret/${raporId}${qs ? `?${qs}` : ""}`;
}

/**
 * Rapor aç: önce FastAPI ile okundu işaretle, sonra PHP URL'yi user_id ile aç.
 * (href render anında user_id kaçmasın diye tıklamada çalışır)
 */
/* hydrate session user */
getSessionUserId();

export async function openGunlukRapor(isEmriNo, userId, markRead = true) {
  const uid = userId != null && Number(userId) > 0 ? Number(userId) : getSessionUserId();
  const no = String(isEmriNo || "");
  if (!no) return;
  try {
    if (markRead !== false && uid != null) {
      await api.post(`/is-emirleri/${encodeURIComponent(no)}/mark-read`);
    }
  } catch (_) {
    /* PHP tarafı da dener; API fail engellemesin */
  }
  const url = gunlukRaporUrl(no, uid);
  window.open(url, "_blank", "noopener,noreferrer");
}

// Cloud PDF (nginx /pdf/ → /api/v1/pdf/)
export const pdfUrl = (path) => `/pdf/${String(path).replace(/^\//, "")}`;

function _pdfRel(path) {
  return String(path)
    .replace(/^\//, "")
    .replace(/^servis\/pdf\//, "")
    .replace(/^pdf\//, "")
    .replace(/^servis\/api\/v1\/pdf\//, "")
    .replace(/^api\/v1\/pdf\//, "");
}

/** Auth'lu PDF blob URL (iframe / window.open için). */
export async function fetchPdfObjectUrl(path) {
  const rel = _pdfRel(path);
  const { data } = await api.get(`/pdf/${rel}`, { responseType: "blob" });
  return URL.createObjectURL(data);
}

/** PDF'i yeni sekmede aç — Bearer ile indirip blob açar (auth zorunlu). */
export async function openPdf(path) {
  const url = await fetchPdfObjectUrl(path);
  window.open(url, "_blank", "noopener,noreferrer");
}

export default api;
